import { HttpError } from '@littlebird/auth-client';
import {
  PROJECT_FILE_EXTENSIONS,
  getProjectFileStatus,
  validateProjectFileSelection,
  type ProjectFileStatusDisplay,
} from '@littlebird/ui-utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'motion/react';
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { FileDropZone } from '@/components/blocks/FileDropZone';
import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, Skeleton, Text, XStack, YStack } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import {
  appendExtensionToFilenameStem,
  deleteProjectFileMutation,
  type ProjectFile,
  projectFilesQuery,
  uploadProjectFileMutation,
} from './project-files.queries';

const ACCEPT_ATTR = PROJECT_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const DOCUMENT_LINE_Y_POSITIONS = [42, 51, 60];
const CHAT_ROWS = [
  { y: 29, delay: 0.46 },
  { y: 57, delay: 0.58 },
];

function ProjectFileStatus({ status }: { status: ProjectFileStatusDisplay }) {
  return (
    <Text
      className={cn(
        'h-4 truncate text-xs',
        status.tone === 'danger' ? 'text-danger-11' : 'text-foreground-muted'
      )}
      role="status"
      aria-live="polite"
    >
      {status.label}
    </Text>
  );
}

function ProjectFileLeadingIcon({
  status,
}: {
  status: ProjectFileStatusDisplay | null;
}) {
  const className = 'mt-0.5 size-4 shrink-0';

  if (status?.tone === 'danger') {
    return (
      <Icons.warn className={cn(className, 'text-danger-11')} aria-hidden />
    );
  }
  if (status?.spinning) {
    return (
      <Icons.loader
        className={cn(className, 'animate-spin text-foreground-muted')}
        aria-hidden
      />
    );
  }
  return (
    <Icons.textFile
      className={cn(className, 'text-foreground-muted')}
      aria-hidden
    />
  );
}

function getProjectFileSortPriority(file: ProjectFile): number {
  const status = getProjectFileStatus(file);
  if (status?.tone === 'danger') return 0;
  if (status?.spinning) return 1;
  return 2;
}

type ProjectFileRowProps = {
  file: ProjectFile;
  onDelete: () => void;
  deleting: boolean;
  canEditContent: boolean;
};

function ProjectFileRow({
  file,
  onDelete,
  deleting,
  canEditContent,
}: ProjectFileRowProps) {
  const status = getProjectFileStatus(file);

  return (
    <YStack className="group min-h-14 justify-center rounded-lg px-3 py-2.5 transition-colors duration-150 hover:bg-surface-hover focus-within:bg-surface-hover">
      <XStack className="items-start gap-2.5">
        <ProjectFileLeadingIcon status={status} />
        <YStack className="min-w-0 flex-1 gap-0.5">
          <Text className="truncate text-sm">{file.filename}</Text>
          {status ? (
            <ProjectFileStatus status={status} />
          ) : file.summary ? (
            <Text className="truncate text-xs text-foreground-muted">
              {file.summary}
            </Text>
          ) : null}
        </YStack>
        {canEditContent && (
          <Button
            size="icon"
            variant="ghost"
            tooltip="Delete file"
            aria-label={`Delete ${file.filename}`}
            onClick={onDelete}
            isLoading={deleting}
            className={cn(
              'self-center opacity-0 touch:opacity-100 transition-opacity duration-150',
              'group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100',
              deleting && 'opacity-100'
            )}
          >
            <Icons.trash />
          </Button>
        )}
      </XStack>
    </YStack>
  );
}

const ProjectFilesEmptyStateAnimation = () => {
  const shouldReduceMotion = useReducedMotion() ?? false;

  return (
    <motion.svg
      viewBox="0 0 180 100"
      className="h-[5.625rem] w-40 overflow-visible text-foreground-muted"
      aria-hidden
      initial={
        shouldReduceMotion ? false : { opacity: 0, y: 8, filter: 'blur(4px)' }
      }
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
    >
      <motion.g
        initial={
          shouldReduceMotion ? false : { x: -45, rotate: -8, opacity: 0 }
        }
        animate={{ x: 0, rotate: 0, opacity: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.7,
          delay: shouldReduceMotion ? 0 : 0.05,
          ease: EASE_OUT,
        }}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <rect
          x={34}
          y={19}
          width={54}
          height={62}
          rx={4}
          fill="currentColor"
          fillOpacity={0.08}
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={0.75}
        />
        <path
          d="M76 19v12h12"
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={0.75}
        />
        {DOCUMENT_LINE_Y_POSITIONS.map((y, index) => (
          <motion.rect
            key={y}
            x={45}
            y={y}
            width={index === DOCUMENT_LINE_Y_POSITIONS.length - 1 ? 22 : 32}
            height={2.5}
            rx={1.25}
            fill="currentColor"
            fillOpacity={0.17}
            initial={shouldReduceMotion ? false : { opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{
              duration: shouldReduceMotion ? 0 : 0.3,
              delay: shouldReduceMotion ? 0 : 0.4 + index * 0.08,
              ease: EASE_OUT,
            }}
            style={{ transformBox: 'fill-box', transformOrigin: 'left' }}
          />
        ))}
      </motion.g>

      {CHAT_ROWS.map(({ y, delay }) => (
        <motion.g
          key={y}
          initial={shouldReduceMotion ? false : { x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{
            duration: shouldReduceMotion ? 0 : 0.55,
            delay: shouldReduceMotion ? 0 : delay,
            ease: EASE_OUT,
          }}
        >
          <rect
            x={116}
            y={y}
            width={34}
            height={15}
            rx={3}
            fill="currentColor"
            fillOpacity={0.07}
            stroke="currentColor"
            strokeOpacity={0.16}
            strokeWidth={0.75}
          />
          <rect
            x={123}
            y={y + 6}
            width={18}
            height={2.5}
            rx={1.25}
            fill="currentColor"
            fillOpacity={0.15}
          />
        </motion.g>
      ))}

      <motion.path
        d="M88 44c13 0 15-8 28-8"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={1}
        strokeLinecap="round"
        initial={shouldReduceMotion ? false : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.45,
          delay: shouldReduceMotion ? 0 : 0.78,
          ease: EASE_OUT,
        }}
      />
      <motion.path
        d="M88 57c13 0 15 7 28 7"
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={1}
        strokeLinecap="round"
        initial={shouldReduceMotion ? false : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.45,
          delay: shouldReduceMotion ? 0 : 0.9,
          ease: EASE_OUT,
        }}
      />
    </motion.svg>
  );
};

const ProjectFilesEmptyState = () => (
  <Empty.Root>
    <Empty.Media>
      <ProjectFilesEmptyStateAnimation />
    </Empty.Media>
    <Empty.Header>
      <Empty.Title>Give your project more context</Empty.Title>
      <Empty.Description>
        Upload PDFs, documents, or text files for this project's chats to
        reference.
      </Empty.Description>
    </Empty.Header>
  </Empty.Root>
);

export function ProjectFilesTab({
  projectId,
  canEditContent,
}: {
  projectId: string;
  canEditContent: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const { data: files, isLoading } = useQuery(projectFilesQuery(projectId));
  const upload = useMutation(uploadProjectFileMutation(projectId));
  const remove = useMutation(deleteProjectFileMutation(projectId));
  const orderedFiles = files
    ? [...files].sort(
        (left, right) =>
          getProjectFileSortPriority(left) - getProjectFileSortPriority(right)
      )
    : [];

  const uploadFiles = async (selected: File[]) => {
    const uploadedFilenames = new Set(
      files?.map((file) => file.filename.toLowerCase())
    );
    const uniqueFiles = selected.filter((file) => {
      const uploadedFilename = appendExtensionToFilenameStem(
        file.name
      ).toLowerCase();
      if (uploadedFilenames.has(uploadedFilename)) {
        toast.error(`${file.name} is already in this project`);
        return false;
      }
      uploadedFilenames.add(uploadedFilename);
      return true;
    });

    const { accepted, errors, limitReached } = validateProjectFileSelection({
      selected: uniqueFiles,
      existingCount: files?.length ?? 0,
    });
    if (limitReached) {
      toast.error('File limit reached', { description: errors[0] });
      return;
    }
    errors.forEach((message) => toast.error(message));

    for (const file of accepted) {
      const filename = file.name.toLowerCase();
      if (
        !PROJECT_FILE_EXTENSIONS.some((extension) =>
          filename.endsWith(`.${extension}`)
        )
      ) {
        toast.error(`${file.name} isn't a supported project file.`);
        continue;
      }
      try {
        await upload.mutateAsync(file);
      } catch (error) {
        if (error instanceof HttpError) return;
        toast.error(
          error instanceof Error
            ? error.message
            : `Failed to upload ${file.name}`
        );
      }
    }
  };

  const handleFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const selected = Array.from(input.files ?? []);
    input.value = '';
    await uploadFiles(selected);
  };

  const hasDraggedFiles = (event: DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer.types).includes('Files');

  const handleDrag = (event: DragEvent<HTMLElement>) => {
    if (!canEditContent || !hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!canEditContent || !hasDraggedFiles(event)) return;
    event.preventDefault();
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    )
      return;
    setIsDraggingFile(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!canEditContent || !hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsDraggingFile(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <YStack
      data-testid="project-files-drop-zone"
      data-project-file-drop-zone={canEditContent ? '' : undefined}
      className="relative flex-1"
      onDragEnter={handleDrag}
      onDragLeave={handleDragLeave}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <FileDropZone
        show={isDraggingFile}
        message="Drop files here to upload to project"
      />
      <YStack className={cn('flex-1 gap-4', isDraggingFile && 'opacity-10')}>
        <XStack className="items-center justify-between gap-3">
          <Text className="text-sm text-foreground-muted">
            Reference files for this project's chats.
          </Text>
          {canEditContent && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={ACCEPT_ATTR}
                onChange={handleFilesSelected}
              />
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  trackEvent('PROJECT_FILE_UPLOAD_CLICKED');
                  fileInputRef.current?.click();
                }}
                disabled={isLoading || upload.isPending}
              >
                {upload.isPending ? (
                  <Icons.loader className="size-3.5 animate-spin" />
                ) : (
                  <Icons.upload className="size-3.5" />
                )}
                Upload files
              </Button>
            </>
          )}
        </XStack>

        {isLoading ? (
          <YStack className="gap-2">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </YStack>
        ) : !files || files.length === 0 ? (
          <ProjectFilesEmptyState />
        ) : (
          <YStack className="gap-2">
            {orderedFiles.map((file) => (
              <ProjectFileRow
                key={file.id}
                file={file}
                onDelete={async () => {
                  trackEvent('PROJECT_FILE_DELETE_CLICKED');
                  const confirmed = await ModalsManager.showResolvable(
                    ConfirmDialog,
                    {
                      title: 'Delete file',
                      description: `Are you sure you want to delete '${file.filename}'?`,
                      confirmText: 'Delete',
                    }
                  );
                  if (confirmed) remove.mutate(file.id);
                }}
                deleting={remove.isPending && remove.variables === file.id}
                canEditContent={canEditContent}
              />
            ))}
          </YStack>
        )}
      </YStack>
    </YStack>
  );
}
